import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Web3Provider } from "./contexts/Web3Context";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import OnerikLogoSmall from "./components/OnerikLogoSmall";
import LanguageSelector from "./components/LanguageSelector";
import NotificationCenter from "./components/NotificationCenter";
import ThemeToggle from "./components/ThemeToggle";
import OyonoChat from "./components/OyonoChat";
import Landing from "./pages/Landing";
import Swap from "./pages/Swap";
import SwapConfirmation from "./pages/SwapConfirmation";
import Liquidity from "./pages/Liquidity";
import Staking from "./pages/Staking";
import Farming from "./pages/Farming";
import Banking from "./pages/Banking";
import Portfolio from "./pages/Portfolio";
import Dashboard from "./pages/Dashboard";
import Overview from "./pages/Overview";
import Wallets from "./pages/Wallets";
import GetStarted from "./pages/GetStarted";
import Cards from "./pages/Cards";
import AdminDashboard from "./pages/AdminDashboard";
import Security from "./pages/Security";
import VIPMima from "./pages/VIPMima";
import TransactionHistory from "./pages/TransactionHistory";
import SendMoney from "./pages/SendMoney";
import MerchantPortal from "./pages/MerchantPortal";
import MerchantAdmin from "./pages/MerchantAdmin";
import { Toaster } from "./components/ui/sonner";
import { Wallet, ArrowDownUp, TrendingUp, CreditCard, BarChart3, Rocket, Menu, X, LogOut, Settings, Shield, Crown, History, Store } from "lucide-react";

// RTL languages
const RTL_LANGUAGES = ['ar'];

// Admin emails for showing admin link
const ADMIN_EMAILS = ['anthonyb@novitrade.tech', 'admin@onerik.com'];

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505]">
        <OnerikLogoSmall />
        <div className="mt-4 w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-2 text-white/40 text-sm">Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const NavLink = ({ to, children, icon: Icon, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap ${
        isActive
          ? "bg-primary text-white"
          : "text-white/70 hover:text-white hover:bg-white/5"
      }`}
    >
      {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
      <span className="hidden xl:inline">{children}</span>
    </Link>
  );
};

const MobileNavLink = ({ to, children, icon: Icon, onClick }) => {
  const location = useLocation();
  const { isDark } = useTheme();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
        isActive
          ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white"
          : isDark 
            ? "text-white/70 hover:text-white hover:bg-white/10"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
      }`}
    >
      {Icon && <Icon className={`w-5 h-5 ${isActive ? 'text-white' : isDark ? 'text-purple-400' : 'text-purple-600'}`} />}
      <span>{children}</span>
    </Link>
  );
};

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const [kycVerified, setKycVerified] = useState(false);
  
  // Handle RTL for Arabic
  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRTL]);

  // Check KYC status
  useEffect(() => {
    const checkKycStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kyc/sumsub/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setKycVerified(data.kyc_verified || data.status === 'GREEN');
        }
      } catch (error) {
        console.log('KYC status check failed');
      }
    };
    
    if (isAuthenticated) {
      checkKycStatus();
    }
  }, [isAuthenticated]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Don't show navigation on landing page (login/register)
  const isLandingPage = location.pathname === '/';

  // Landing page always stays dark
  if (isLandingPage) {
    return (
      <div className="min-h-screen bg-[#050505] relative">
        <div
          className="fixed inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' opacity=\'0.05\'/%3E%3C/svg%3E")',
          }}
        ></div>
        <main>{children}</main>
      </div>
    );
  }

  // Theme-aware background colors
  const bgColor = isDark ? 'bg-[#050505]' : 'bg-[#f5f5f7]';
  const navBg = isDark ? 'bg-black/80' : 'bg-white/90';
  const navBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/70' : 'text-gray-600';
  const textSubtle = isDark ? 'text-white/40' : 'text-gray-400';
  const hoverBg = isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100';
  const menuBg = isDark ? 'bg-black/90' : 'bg-white/95';

  return (
    <div className={`min-h-screen ${bgColor} relative transition-colors duration-300`}>
      {isDark && (
        <div
          className="fixed inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' opacity=\'0.05\'/%3E%3C/svg%3E")',
          }}
        ></div>
      )}

      {/* Universal Navigation - Hamburger Menu */}
      <nav className="fixed top-4 left-4 right-4 z-50">
        <div className="max-w-7xl mx-auto">
          <div className={`${navBg} backdrop-blur-2xl border ${navBorder} rounded-2xl px-4 py-3 shadow-2xl flex items-center justify-between transition-colors duration-300`}>
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-xl ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-all`}
                data-testid="menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <Link to="/portfolio" className="flex items-center gap-2" onClick={closeMobileMenu}>
                <OnerikLogoSmall />
                <span className={`text-lg font-bold ${textColor}`}>Onerik</span>
              </Link>
            </div>

            {/* Right: Theme Toggle, Language, Notifications, User, Logout */}
            <div className="flex items-center gap-2 sm:gap-3">
              <ThemeToggle />
              <LanguageSelector />
              <NotificationCenter />
              {user && (
                <span className={`${textMuted} text-sm hidden sm:block truncate max-w-[100px]`}>
                  {user.full_name?.split(' ')[0]}
                </span>
              )}
              <button
                onClick={logout}
                className={`p-2 rounded-xl ${textMuted} hover:${textColor} ${hoverBg} transition-all`}
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Dropdown Menu */}
          {mobileMenuOpen && (
            <div className={`mt-2 ${menuBg} backdrop-blur-2xl border ${navBorder} rounded-2xl p-4 shadow-2xl animate-in slide-in-from-top-2 duration-200 transition-colors`}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                <MobileNavLink to="/portfolio" icon={BarChart3} onClick={closeMobileMenu}>
                  {t('nav.dashboard')}
                </MobileNavLink>
                {/* Only show Get Started if KYC is not verified */}
                {!kycVerified && (
                  <MobileNavLink to="/get-started" icon={Rocket} onClick={closeMobileMenu}>
                    {t('nav.getStarted')}
                  </MobileNavLink>
                )}
                <MobileNavLink to="/swap" icon={ArrowDownUp} onClick={closeMobileMenu}>
                  {t('nav.swap')}
                </MobileNavLink>
                <MobileNavLink to="/staking" icon={TrendingUp} onClick={closeMobileMenu}>
                  {t('nav.stake')}
                </MobileNavLink>
                <MobileNavLink to="/banking" icon={CreditCard} onClick={closeMobileMenu}>
                  {t('nav.buy')}
                </MobileNavLink>
                <MobileNavLink to="/cards" icon={Wallet} onClick={closeMobileMenu}>
                  {t('nav.cards')}
                </MobileNavLink>
                <MobileNavLink to="/transactions" icon={History} onClick={closeMobileMenu}>
                  History
                </MobileNavLink>
                <MobileNavLink to="/vip" icon={Crown} onClick={closeMobileMenu}>
                  VIP
                </MobileNavLink>
                <MobileNavLink to="/security" icon={Shield} onClick={closeMobileMenu}>
                  {t('nav.security')}
                </MobileNavLink>
                <MobileNavLink to="/merchant" icon={Store} onClick={closeMobileMenu}>
                  Merchant
                </MobileNavLink>
                {user && ADMIN_EMAILS.includes(user.email) && (
                  <MobileNavLink to="/admin" icon={Settings} onClick={closeMobileMenu}>
                    {t('nav.admin')}
                  </MobileNavLink>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="pt-20">{children}</main>

      <footer className={`relative mt-6 border-t ${navBorder} py-4 transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className={`${textSubtle} text-xs`}>
            © 2026 Onerik Digital Banking. All rights reserved. | Secure • Regulated • Global
          </p>
        </div>
      </footer>
    </div>
  );
};

// Main App Content with routing
const AppContent = () => {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/get-started" element={<ProtectedRoute><GetStarted /></ProtectedRoute>} />
        {/* Redirect old routes to new unified page */}
        <Route path="/onboarding" element={<Navigate to="/get-started" replace />} />
        <Route path="/kyc" element={<Navigate to="/get-started" replace />} />
        <Route path="/swap" element={<ProtectedRoute><Swap /></ProtectedRoute>} />
        <Route path="/swap/confirmation" element={<ProtectedRoute><SwapConfirmation /></ProtectedRoute>} />
        <Route path="/liquidity" element={<ProtectedRoute><Liquidity /></ProtectedRoute>} />
        <Route path="/staking" element={<ProtectedRoute><Staking /></ProtectedRoute>} />
        <Route path="/farming" element={<ProtectedRoute><Farming /></ProtectedRoute>} />
        <Route path="/banking" element={<ProtectedRoute><Banking /></ProtectedRoute>} />
        <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
        <Route path="/vip" element={<ProtectedRoute><VIPMima /></ProtectedRoute>} />
        <Route path="/wallets" element={<ProtectedRoute><Wallets /></ProtectedRoute>} />
        <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
        <Route path="/overview" element={<ProtectedRoute><Overview /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><TransactionHistory /></ProtectedRoute>} />
        <Route path="/send-money" element={<ProtectedRoute><SendMoney /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
        <Route path="/merchant" element={<MerchantPortal />} />
        <Route path="/merchant/admin" element={<MerchantAdmin />} />
      </Routes>
    </Layout>
  );
};

// OYONO Support Bot - appears on all pages except landing
const OyonoWrapper = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  // Don't show on landing page (login/signup)
  if (location.pathname === '/' || !isAuthenticated) {
    return null;
  }
  
  return <OyonoChat />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Web3Provider>
          <BrowserRouter>
            <AppContent />
            <OyonoWrapper />
            <Toaster position="top-right" />
          </BrowserRouter>
        </Web3Provider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
